/**
 * Honest map / list mini camera host.
 * Soft≠Live: Live only with authoritative WHEP ticket OR remux AU (media_played session).
 * GLL-18: single-flight wait_ms pull Soft≠ setInterval stampede Soft≠ Date.now PTS jumps.
 */
import { useEffect, useRef } from "react";
import { html, Ico } from "./ui.js";
import { pullLiveViewAus } from "./udock-api.js";
import {
  AU_EMPTY_FLOOR_MS,
  AU_LAGGY_QUEUE,
  AU_TS_STEP_US,
  AU_WAIT_MS_MINI,
  annexBIfNeeded,
  b64ToU8,
  shouldDropDelta,
  startSingleFlightAuLoop,
} from "./live-au-pump.js";

/**
 * @param {{
 *  label?: string,
 *  kind?: string,
 *  live?: boolean,
 *  reason?: string,
 *  whepUrl?: string,
 *  sessionId?: string,
 *  deviceId?: string,
 *  onOpen?: () => void,
 * }} props
 */
export function CameraMiniPlayer({
  label = "Camera",
  kind = "device",
  live = false,
  reason = "",
  whepUrl = "",
  sessionId = "",
  deviceId = "",
  onOpen,
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const pcRef = useRef(null);
  const url = String(whepUrl || "").trim();
  const sid = String(sessionId || "").trim();
  const did = String(deviceId || "").trim();
  /* AU is the product pixel plane. WHEP tickets exist while ICE stays closed. */
  const useAu = !!(live && sid && did);
  const useWhep = !!(live && !useAu && url);

  useEffect(() => {
    let dead = false;
    const video = videoRef.current;
    if (!useWhep || !video || typeof RTCPeerConnection === "undefined") {
      return undefined;
    }
    const pc = new RTCPeerConnection({ iceServers: [] });
    pcRef.current = pc;
    pc.addTransceiver("video", { direction: "recvonly" });
    const pinAr = () => {
      try {
        const w = Number(video.videoWidth);
        const h = Number(video.videoHeight);
        if (w > 0 && h > 0) {
          const host = video.closest(".camera-mini__screen") || video.parentElement;
          if (host) host.style.setProperty("--cam-mini-ar", `${Math.round(w)} / ${Math.round(h)}`);
        }
      } catch { /* */ }
    };
    try { video.addEventListener("loadedmetadata", pinAr); } catch { /* */ }
    pc.ontrack = (ev) => {
      if (dead) return;
      try {
        video.srcObject = ev.streams[0] || null;
        video.muted = true;
        video.playsInline = true;
        video.play().catch(() => {});
        pinAr();
      } catch { /* */ }
    };
    (async () => {
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/sdp" },
          body: offer.sdp || "",
          credentials: "include",
        });
        if (!res.ok || dead) return;
        const answer = await res.text();
        if (dead) return;
        await pc.setRemoteDescription({ type: "answer", sdp: answer });
      } catch { /* fail closed — status chip stays honest */ }
    })();
    return () => {
      dead = true;
      try { pc.close(); } catch { /* */ }
      pcRef.current = null;
      try { video.srcObject = null; } catch { /* */ }
    };
  }, [useWhep, url]);

  useEffect(() => {
    let dead = false;
    const canvas = canvasRef.current;
    if (!useAu || !canvas || typeof VideoDecoder === "undefined") return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    let after = 0;
    let configured = false;
    let needKey = true;
    let tsUs = 0;
    const decoder = new VideoDecoder({
      output: (frame) => {
        if (dead) {
          try { frame.close(); } catch { /* */ }
          return;
        }
        try {
          const w = frame.displayWidth || frame.codedWidth || 320;
          const h = frame.displayHeight || frame.codedHeight || 180;
          if (canvas.width !== w) canvas.width = w;
          if (canvas.height !== h) canvas.height = h;
          const host = canvas.closest(".camera-mini__screen") || canvas.parentElement;
          if (host && canvas.width > 0 && canvas.height > 0) {
            host.style.setProperty(
              "--cam-mini-ar",
              `${Math.round(canvas.width)} / ${Math.round(canvas.height)}`,
            );
          }
          ctx.drawImage(frame, 0, 0, canvas.width, canvas.height);
        } catch { /* */ }
        try { frame.close(); } catch { /* */ }
      },
      error: () => { /* fail closed Soft≠ invent Soft pixels */ },
    });
    const tick = async () => {
      if (dead) return;
      const out = await pullLiveViewAus(did, sid, {
        max_n: 16,
        after_seq: after,
        wait_ms: AU_WAIT_MS_MINI,
      });
      if (!out || out.ok !== true || dead) return;
      const j = out.data || {};
      const laggy = typeof decoder.decodeQueueSize === "number" && decoder.decodeQueueSize > AU_LAGGY_QUEUE;
      for (const it of j.items || []) {
        after = Math.max(after, Number(it.seq || 0));
        if (!it.nal_b64) continue;
        const isKey = !!it.key;
        if (shouldDropDelta(needKey, isKey, laggy)) continue;
        const payload = annexBIfNeeded(b64ToU8(it.nal_b64));
        try {
          if (!configured && isKey) {
            const codec = String(it.codec || j.codec || "avc1.42C01E");
            const hevc = /hev|hvc|hevc|h265/i.test(codec);
            decoder.configure({
              codec,
              optimizeForLatency: true,
              ...(hevc ? { hevc: { format: "annexb" } } : { avc: { format: "annexb" } }),
            });
            configured = true;
            needKey = false;
          }
          if (!configured) continue;
          if (isKey) needKey = false;
          tsUs += AU_TS_STEP_US;
          decoder.decode(
            new EncodedVideoChunk({
              type: isKey ? "key" : "delta",
              timestamp: tsUs,
              data: payload,
            }),
          );
        } catch {
          needKey = true;
        }
      }
    };
    const stop = startSingleFlightAuLoop({
      tick,
      isDead: () => dead,
      waitMs: AU_WAIT_MS_MINI,
      emptyFloorMs: AU_EMPTY_FLOOR_MS,
    });
    return () => {
      dead = true;
      try { stop(); } catch { /* */ }
      try { decoder.close(); } catch { /* */ }
    };
  }, [useAu, sid, did]);

  const status = live ? "LIVE" : (reason || "Not live");
  const _showMedia = useWhep || useAu;
  return html`<button type="button" className="camera-mini"
      data-live=${live ? "1" : "0"} data-kind=${kind}
      data-player=${useWhep ? "whep" : (useAu ? "webcodecs_au" : "none")}
      aria-label=${`${kind} ${label}: ${status}`}
      onClick=${() => onOpen && onOpen()}>
    <span className="camera-mini__screen" aria-hidden="true">
      ${useWhep
        ? html`<video ref=${videoRef} muted playsInline/>`
        : useAu
          ? html`<canvas ref=${canvasRef}/>`
          : html`<i><${Ico} d="camera" w=${16}/></i>`}
      <b>${status}</b>
      <u>${String(label || "").slice(0, 18)}</u>
    </span>
    <span className="camera-mini__cap"><i>${kind}</i></span>
  </button>`;
}

export default CameraMiniPlayer;
